<template>
  <div class="orphan-links-tab mx-2 mb-4">
    <div class="alert alert-light border small mb-3 py-2">
      <strong>Orphan links</strong> — opportunities with <code>campaign_id = NULL</code> and an active meeting.
      Client is resolved via the meeting owner (<code>link_event.user_id</code>).
      Without a campaign: no automatic billing and the opportunity detail page may not load.
      This list only shows unbilled meetings from <strong>January 2025</strong> onward.
      Older orphans are excluded and will never be auto-resolved.
      Gray rows cannot be auto-resolved (test data, self-bookings, unresolved client, or no prospect in the meeting).
      Use <strong>Ignore</strong> to permanently hide a row from this list.
      Other rows: Resolve shows a preview of planned actions; nothing is changed until you confirm.
      Campaign priority: already contacted this prospect → already enrolled → newest client campaign.
    </div>

    <div class="card border-0 shadow-sm mb-3">
      <div class="card-body py-2 d-flex flex-wrap align-items-center">
        <div class="d-flex align-items-center mr-3">
          <span class="small text-muted mr-2">Client ID</span>
          <input
            v-model="filters.client_id"
            type="text"
            class="form-control form-control-sm"
            style="width: 100px;"
            placeholder="e.g. 4011"
            @keyup.enter="loadItems"
          />
        </div>
        <button
          type="button"
          class="btn btn-sm btn-outline-primary"
          :disabled="loading"
          @click="loadItems"
        >
          <span v-if="loading" class="spinner-border spinner-border-sm mr-1" />
          Refresh
        </button>
        <label
          v-if="items.length"
          class="mb-0 mr-2 ml-3 small d-flex align-items-center"
        >
          <input
            v-model="bulkResolveConfirm"
            type="checkbox"
            class="mr-1"
            :disabled="bulkResolving"
          />
          Resolve all listed links
        </label>
        <button
          v-if="items.length"
          type="button"
          class="btn btn-sm btn-primary"
          :disabled="!bulkResolveConfirm || bulkResolving || loading"
          @click="resolveAll"
        >
          <span v-if="bulkResolving" class="spinner-border spinner-border-sm mr-1" />
          Resolve all
        </button>
        <span v-if="total !== null" class="small text-muted ml-auto">
          {{ total }} rows shown (max 500)
        </span>
      </div>
    </div>

    <div
      v-if="bulkSummary"
      class="alert alert-info small py-2 mb-3 d-flex align-items-center justify-content-between"
    >
      <span>{{ bulkSummary }}</span>
      <button type="button" class="btn btn-sm btn-link p-0" @click="bulkSummary = ''">
        Dismiss
      </button>
    </div>

    <LoadingSpinner v-if="loading" title="Loading orphan links…" />

    <LoadingSpinner v-else-if="bulkResolving" :title="bulkResolvingTitle" />

    <p v-else-if="!items.length" class="text-muted small mb-0">
      No orphan links with meetings for these filters.
    </p>

    <div v-else class="table-responsive">
      <table class="table table-sm table-hover bg-white border">
        <thead class="thead-light">
          <tr>
            <th>Link</th>
            <th>Client</th>
            <th>Business</th>
            <th>Prospect</th>
            <th>Meeting</th>
            <th>Booker</th>
            <th>Billed</th>
            <th>Likely cause</th>
            <th>Emails</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in items"
            :key="row.link_id"
            :class="rowRowClass(row)"
          >
            <td>
              <a
                :href="'/opportunities/' + row.link_id"
                target="_blank"
                class="font-weight-bold"
              >#{{ row.link_id }}</a>
              <div class="text-muted" style="font-size: 11px;">
                created {{ formatDate(row.link_creation_date) }}
              </div>
            </td>
            <td>
              <template v-if="row.client_id">
                <a
                  :href="'/clients/' + row.client_id"
                  target="_blank"
                >{{ row.client_name }}</a>
                <div class="text-muted" style="font-size: 11px;">#{{ row.client_id }}</div>
              </template>
              <span v-else class="text-danger small">Unresolved</span>
            </td>
            <td>
              <div>{{ row.business_name || '—' }}</div>
              <div class="text-muted" style="font-size: 11px;">{{ row.business_domain }}</div>
            </td>
            <td>
              <div>{{ (row.prospect_name || '').trim() || '—' }}</div>
              <div class="text-muted" style="font-size: 11px;">{{ row.prospect_email }}</div>
            </td>
            <td>
              <div>{{ formatDateTime(row.event_date) }}</div>
              <span
                class="badge badge-light border"
                :class="row.is_past == 1 ? 'text-warning' : 'text-muted'"
              >
                {{ row.is_past == 1 ? 'past' : 'upcoming' }}
              </span>
            </td>
            <td>
              <div>{{ (row.meeting_owner_name || '').trim() || '—' }}</div>
              <div class="text-muted" style="font-size: 11px;">user #{{ row.meeting_owner_user_id }}</div>
            </td>
            <td>
              <span
                class="badge"
                :class="row.is_billed === 'yes' ? 'badge-success' : 'badge-secondary'"
              >
                {{ row.is_billed === 'yes' ? 'Yes' : 'No' }}
              </span>
              <div v-if="row.billed_amount" class="small text-muted">{{ row.billed_amount }} €</div>
            </td>
            <td>
              <span class="badge" :class="causeBadgeClass(row.probable_cause)">
                {{ causeLabel(row.probable_cause) }}
              </span>
              <span v-if="row.is_skipped == 1" class="badge badge-secondary ml-1">{{ skipLabel(row) }}</span>
            </td>
            <td class="small text-muted">
              ↓{{ row.email_responses_count || 0 }} / ↑{{ row.emails_sent_count || 0 }}
            </td>
            <td class="text-nowrap">
              <button
                v-if="row.is_skipped != 1"
                type="button"
                class="btn btn-sm btn-outline-primary mr-1"
                :disabled="resolvingLinkId === row.link_id || bulkResolving || ignoringLinkId === row.link_id"
                @click="openResolvePreview(row)"
              >
                <span v-if="previewLinkId === row.link_id" class="spinner-border spinner-border-sm mr-1" />
                Resolve
              </button>
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                :disabled="ignoringLinkId === row.link_id || bulkResolving || resolvingLinkId === row.link_id"
                @click="ignoreLink(row)"
              >
                <span v-if="ignoringLinkId === row.link_id" class="spinner-border spinner-border-sm mr-1" />
                Ignore
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <Modal name="orphan-link-resolve-modal" :size="'50'" :scrollable="true" @closeModal="closeResolveModal">
      <div class="p-4" v-if="selectedRow">
        <template v-if="modalMode === 'confirm' && resolvePreview">
          <h5 class="mb-3">
            Confirm resolve — link #{{ selectedRow.link_id }}
            <small class="text-muted">{{ resolvePreview.business_name || selectedRow.business_name }}</small>
          </h5>

          <div class="small mb-3">
            <div><strong>Client:</strong> {{ resolvePreview.client_name || '—' }} (#{{ resolvePreview.client_id }})</div>
            <div><strong>Prospect:</strong> {{ (resolvePreview.prospect_name || '').trim() || '—' }} — {{ resolvePreview.prospect_email || '—' }}</div>
            <div><strong>Meeting:</strong> {{ formatDateTime(resolvePreview.event_date || selectedRow.event_date) }}</div>
            <div><strong>Campaign:</strong> #{{ resolvePreview.campaign_id }} — {{ resolvePreview.campaign_name || '—' }}</div>
            <div v-if="resolvePreview.outcome === 'merge'">
              <strong>Outcome:</strong> move meeting to existing link
              <a
                :href="'/opportunities/' + resolvePreview.target_link_id"
                target="_blank"
                class="font-weight-bold"
              >#{{ resolvePreview.target_link_id }}</a>
            </div>
          </div>

          <div class="alert alert-light border small mb-3">
            <strong class="d-block mb-2">Planned actions</strong>
            <ol class="mb-0 pl-3">
              <li v-for="(step, index) in resolvePreview.steps" :key="'step-' + index">{{ step }}</li>
            </ol>
          </div>

          <p class="small text-muted mb-3">{{ resolvePreview.summary }}</p>

          <div class="d-flex justify-content-end">
            <button type="button" class="btn btn-outline-secondary mr-2" :disabled="resolvingLinkId === selectedRow.link_id" @click="closeResolveModal">
              Cancel
            </button>
            <button type="button" class="btn btn-primary" :disabled="resolvingLinkId === selectedRow.link_id" @click="confirmResolve">
              <span v-if="resolvingLinkId === selectedRow.link_id" class="spinner-border spinner-border-sm mr-1" />
              Confirm resolve
            </button>
          </div>
        </template>

        <template v-else>
          <h5 class="mb-3">
            Cannot resolve link #{{ selectedRow.link_id }}
            <small class="text-muted">{{ selectedRow.business_name }}</small>
          </h5>

          <div class="alert alert-warning small mb-3">
            <strong>{{ blockedTitle }}</strong>
            <p class="mb-0 mt-2">{{ blockedMessage }}</p>
          </div>

          <div v-if="blockedReason === 'duplicate_link' && blockedDuplicateLinkId" class="small mb-3">
            Existing opportunity:
            <a
              :href="'/opportunities/' + blockedDuplicateLinkId"
              target="_blank"
              class="font-weight-bold"
            >#{{ blockedDuplicateLinkId }}</a>
          </div>

          <div v-if="blockedReason === 'unresolved_contact' || blockedReason === 'unresolved_prospect'" class="small text-muted mb-3">
            Checked: <code>link_event.business_contact_id</code>, calendar invite email,
            <code>link_business_contact</code>, and <code>email_response</code>.
          </div>

          <div class="d-flex justify-content-end">
            <button type="button" class="btn btn-primary" @click="closeResolveModal">
              OK
            </button>
          </div>
        </template>
      </div>
    </Modal>
  </div>
</template>

<script>
import { targetsService } from "@/_services";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import Modal from "@/components/Common/Modal.vue";

export default {
  name: "OrphanLinksTab",
  components: {
    LoadingSpinner,
    Modal,
  },
  data() {
    return {
      loading: false,
      items: [],
      total: null,
      filters: {
        client_id: "",
      },
      selectedRow: null,
      modalMode: "blocked",
      resolvePreview: null,
      previewLinkId: null,
      blockedReason: "",
      blockedMessage: "",
      blockedDuplicateLinkId: null,
      resolvingLinkId: null,
      ignoringLinkId: null,
      bulkResolveConfirm: false,
      bulkResolving: false,
      bulkSummary: "",
    };
  },
  computed: {
    bulkResolvingTitle() {
      const count = this.items.length;
      return count ? `Resolving ${count} orphan links…` : "Resolving orphan links…";
    },
    blockedTitle() {
      const titles = {
        unresolved_contact: "Meeting contact not found",
        unresolved_prospect: "Meeting prospect not found",
        duplicate_link: "Duplicate opportunity",
        merge_failed: "Merge failed",
        test_prospect: "Test prospect",
        test_client: "Test client",
        self_booking: "Self-booking",
        ignored: "Ignored",
        no_campaign_available: "No campaign",
        unresolved_client: "Client not resolved",
      };
      return titles[this.blockedReason] || "Cannot resolve";
    },
  },
  mounted() {
    this.loadItems();
  },
  methods: {
    loadItems() {
      this.loading = true;
      const params = {
        unbilled_only: 1,
      };
      if (this.filters.client_id && String(this.filters.client_id).trim()) {
        params.client_id = String(this.filters.client_id).trim();
      }
      targetsService.getOrphanLinks(params)
        .then((result) => {
          this.items = (result && result.items) ? result.items : [];
          this.total = result && result.total != null ? result.total : this.items.length;
        })
        .catch(() => {
          this.items = [];
          this.total = 0;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    resolveLink(row) {
      this.resolvingLinkId = row.link_id;
      targetsService.resolveOrphanLink(row.link_id)
        .then((result) => {
          if (result && result.status === "blocked") {
            this.openBlockedModal(row, result);
            return;
          }
          this.closeResolveModal();
          this.loadItems();
        })
        .catch((err) => {
          const message = typeof err === "string" ? err : (err && err.error) || "Failed to resolve link.";
          this.openBlockedModal(row, { reason: "error", message });
        })
        .finally(() => {
          this.resolvingLinkId = null;
        });
    },
    openResolvePreview(row) {
      this.previewLinkId = row.link_id;
      targetsService.previewOrphanLinkResolve(row.link_id)
        .then((result) => {
          if (!result || result.status === "blocked") {
            this.openBlockedModal(row, result || {
              reason: "error",
              message: "Could not build resolve preview.",
            });
            return;
          }
          this.selectedRow = row;
          this.modalMode = "confirm";
          this.resolvePreview = result;
          this.blockedReason = "";
          this.blockedMessage = "";
          this.blockedDuplicateLinkId = null;
          this.$modal.show("orphan-link-resolve-modal");
        })
        .catch((err) => {
          const message = typeof err === "string" ? err : (err && err.error) || "Failed to load resolve preview.";
          this.openBlockedModal(row, { reason: "error", message });
        })
        .finally(() => {
          this.previewLinkId = null;
        });
    },
    confirmResolve() {
      if (!this.selectedRow) {
        return;
      }
      this.resolveLink(this.selectedRow);
    },
    ignoreLink(row) {
      if (!row || !row.link_id) {
        return;
      }
      const reason = row.is_self_booking == 1 || row.skip_reason === "self_booking"
        ? "self_booking"
        : (row.skip_reason === "test_prospect" || row.skip_reason === "test_client"
          ? "historical_test_orphan"
          : "ignored_from_orphan_links_ui");
      this.ignoringLinkId = row.link_id;
      targetsService.ignoreOrphanLink(row.link_id, reason)
        .then(() => {
          this.items = this.items.filter((item) => item.link_id !== row.link_id);
          this.total = this.items.length;
        })
        .catch((err) => {
          const message = typeof err === "string" ? err : (err && err.error) || "Failed to ignore link.";
          this.openBlockedModal(row, { reason: "error", message });
        })
        .finally(() => {
          this.ignoringLinkId = null;
        });
    },
    resolveAll() {
      if (!this.bulkResolveConfirm || !this.items.length) {
        return;
      }
      this.bulkResolving = true;
      this.bulkSummary = "";
      const params = {
        unbilled_only: 1,
      };
      if (this.filters.client_id && String(this.filters.client_id).trim()) {
        params.client_id = String(this.filters.client_id).trim();
      }
      targetsService.resolveAllOrphanLinks(params)
        .then((result) => {
          const resolved = result && result.resolved_count != null ? result.resolved_count : 0;
          const remaining = result
            ? (result.blocked_count || 0) + (result.error_count || 0)
            : 0;
          this.bulkSummary = result && result.message
            ? result.message
            : `${resolved} resolved, ${remaining} could not be resolved.`;
          this.bulkResolveConfirm = false;
          this.loadItems();
        })
        .catch((err) => {
          const message = typeof err === "string" ? err : (err && err.error) || "Bulk resolve failed.";
          this.bulkSummary = message;
        })
        .finally(() => {
          this.bulkResolving = false;
        });
    },
    openBlockedModal(row, result) {
      this.selectedRow = row;
      this.modalMode = "blocked";
      this.resolvePreview = null;
      this.blockedReason = result.reason || "error";
      this.blockedMessage = result.message || "Could not resolve this link.";
      this.blockedDuplicateLinkId = result.duplicate_link_id || null;
      this.$modal.show("orphan-link-resolve-modal");
    },
    closeResolveModal() {
      this.$modal.hide("orphan-link-resolve-modal");
      this.selectedRow = null;
      this.modalMode = "blocked";
      this.resolvePreview = null;
      this.blockedReason = "";
      this.blockedMessage = "";
      this.blockedDuplicateLinkId = null;
    },
    rowRowClass(row) {
      if (Number(row.is_skipped) === 1) {
        return "orphan-row-skipped";
      }
      if (row.is_billed === "no" && Number(row.is_past) === 1) {
        return "table-warning";
      }
      if (!row.client_id) {
        return "table-danger";
      }
      return "";
    },
    skipLabel(row) {
      const map = {
        test_prospect: "Test",
        test_client: "Test",
        self_booking: "Self-booking",
        ignored: "Ignored",
        unresolved_client: "No client",
        unresolved_prospect: "No prospect",
        unresolved_contact: "No prospect",
      };
      return map[row.skip_reason] || "Skipped";
    },
    causeLabel(cause) {
      const map = {
        email_inbound: "Inbound email",
        email_outbound_only: "Outbound email only",
        manual_or_phone: "Manual / phone",
        self_booking: "Self-booking",
      };
      return map[cause] || cause || "—";
    },
    causeBadgeClass(cause) {
      if (cause === "email_inbound") return "badge-info";
      if (cause === "email_outbound_only") return "badge-primary";
      if (cause === "self_booking") return "badge-dark";
      return "badge-secondary";
    },
    formatDate(value) {
      if (!value) return "—";
      return String(value).substring(0, 10);
    },
    formatDateTime(value) {
      if (!value) return "—";
      return String(value).replace("T", " ").substring(0, 16);
    },
  },
};
</script>

<style scoped>
.orphan-links-tab code {
  font-size: 11px;
}

.orphan-links-tab tr.orphan-row-skipped td {
  color: #6c757d;
  background-color: #f1f3f5;
  text-decoration: line-through;
}

.orphan-links-tab tr.orphan-row-skipped:hover td {
  background-color: #e9ecef;
}

.orphan-links-tab tr.orphan-row-skipped a {
  color: #868e96;
  pointer-events: none;
  cursor: default;
}
</style>
